<template>
  <b-modal
    ref="modal"
    v-model="local_show"
    dialog-class="synaplus-cosmo-h-720"
    content-class="d-flex h-100"
    body-class="min-h-0 d-flex flex-column"
    :title="action_title"
    size="lg"
    :ok-title="action_title"
    @ok="ok_clicked"
  >
    <b-row class="my-1 align-items-center">
      <b-col cols="2">{{ $t('filename') }}</b-col>
      <b-col
        ><b-form-input v-model="local_filename" @keydown.enter.native="validate"
      /></b-col>
    </b-row>
    <b-row class="my-1 align-items-center">
      <b-col cols="2">{{ $t('directory') }}</b-col>
      <b-col>
        <div class="d-flex flex-row align-items-start">
          <b-button
            v-c-tooltip="$t('enter_location_manually')"
            :pressed.sync="show_manual_directory_input"
            class="mr-3"
            variant="outline-secondary"
          >
            <span class="fas fa-pen" />
          </b-button>
          <div style="overflow-x: auto;" class="flex-w-stretch">
            <b-button-toolbar class="d-flex flex-row align-items-stretch">
              <b-button-group>
                <b-button class="text-nowrap" @click="go_to(-1)">
                  <span class="fas fa-hdd" />
                </b-button>
                <b-button
                  v-for="(element, index) in breadcrumb_items.slice(0, -1)"
                  :key="index"
                  class="text-nowrap"
                  @click="go_to(index)"
                >
                  {{ element }}
                </b-button>
                <b-button
                  v-if="breadcrumb_items.length >= 1"
                  class="text-nowrap"
                  disabled
                >
                  {{ breadcrumb_items[breadcrumb_items.length - 1] }}
                </b-button>
              </b-button-group>
            </b-button-toolbar>
          </div>
          <b-button
            v-c-tooltip="$t('mkdir')"
            class="ml-3"
            variant="outline-secondary"
            @click="mkdir"
          >
            <span class="fas fa-folder-plus" />
          </b-button>
        </div>
      </b-col>
    </b-row>
    <b-alert :show="alert_text !== ''" class="mx-1" variant="danger">
      {{ $t(alert_text) }}
    </b-alert>

    <b-form-input
      v-if="show_manual_directory_input"
      v-model="manual_directory"
      class="mx-1"
      @keydown.enter.native="manual_directory_validated"
    />

    <c-busy :show="fetching_listing" class="flex-h-stretch">
      <b-table
        ref="table"
        borderless
        fixed
        small
        hover
        sticky-header
        selectable
        select-mode="single"
        class="h-100"
        style="max-height: none;"
        :items="listing"
        :fields="listing_fields"
        :sort-compare="directory_listing_sort_compare"
        primary-key="rid"
        @row-selected="row_selected"
        @row-dblclicked="row_double_clicked"
        @row-hovered="row_hovered"
        @row-unhovered="row_unhovered"
      >
        <template v-slot:cell(name)="data">
          <c-overlay
            :ref="'name_cell_' + data.item.rid"
            :overlay="data.item.visibility"
          >
            <div
              v-c-tooltip="data.item.name"
              class="text-truncate user-select-none"
            >
              <span class="mx-1" :class="icon_classes(data.item.is_dir)" />
              {{ data.item.name }}
            </div>
            <template #overlay>
              <div class="flex-w-stretch">
                <b-form
                  v-if="
                    data.index === selected_index &&
                      editing_entry_index === selected_index
                  "
                  inline
                >
                  <b-form-input
                    v-model="entry_input"
                    placeholder="New name"
                    size="sm"
                    class="flex-w-stretch"
                  ></b-form-input>
                </b-form>
                <div
                  v-else
                  v-c-tooltip="data.item.name"
                  class="text-truncate user-select-none"
                >
                  <span class="mx-1" :class="icon_classes(data.item.is_dir)" />
                  {{ data.item.name }}
                </div>
              </div>

              <b-button-group
                v-if="
                  data.index === selected_index &&
                    editing_entry_index === selected_index
                "
              >
                <b-button
                  v-c-tooltip="$t('confirm_renaming')"
                  size="sm"
                  class="bg-white"
                  variant="outline-secondary"
                  @click="validate_entry_input"
                >
                  <span class="fas fa-check fa-xs" />
                </b-button>
                <b-button
                  v-c-tooltip="$t('cancel_renaming')"
                  size="sm"
                  class="bg-white"
                  variant="outline-secondary"
                  @click="cancel_entry_input"
                >
                  <span class="fas fa-times fa-xs" />
                </b-button>
              </b-button-group>
              <b-button-group v-else>
                <b-button
                  v-c-tooltip="$t('rename')"
                  size="sm"
                  class="bg-white"
                  variant="outline-secondary"
                  @click="show_entry_input(data.index)"
                >
                  <span class="fas fa-pen fa-xs" />
                </b-button>
                <b-button
                  v-c-tooltip="$t('delete')"
                  size="sm"
                  class="bg-white"
                  variant="outline-secondary"
                  @click="delete_entry(data.index)"
                >
                  <span class="fas fa-trash fa-xs" />
                </b-button>
              </b-button-group>
            </template>
          </c-overlay>
        </template>

        <template v-slot:cell(last_modified_dt)="data">
          <div
            v-c-tooltip="data.item.tooltip_last_modified_dt"
            class="text-truncate user-select-none"
          >
            {{ data.item.formatted_last_modified_dt }}
          </div>
        </template>

        <template v-slot:cell(size)="data">
          <div
            v-c-tooltip="data.item.tooltip_size"
            class="text-truncate user-select-none"
          >
            {{ data.item.formatted_size }}
          </div>
        </template>
      </b-table>
    </c-busy>
  </b-modal>
</template>

<i18n>
{
  "en": {
    "load": "Load",
    "save": "Save",
    "filename": "Filename",
    "directory": "Directory",
    "enter_location_manually": "Enter location manually...",
    "nonexisting_path": "The entered path doesn't exist",
    "folder_creation_permission_denied": "Permission denied for creating folder",
    "cwra_directory_listing_permission_denied": "Permission denied for listing directory",
    "unknown_error": "Unknown error",
    "mkdir": "Create new folder",
    "new_folder": "New folder",
    "confirm_deletion": "Are you sure you want to delete file/directory {entry_name} in folder {path} ?",
    "item": "item | items",
    "unknown_number_of_items": "? item(s)",
    "confirm_renaming": "Confirm renaming",
    "cancel_renaming": "Cancel renaming",
    "rename": "Rename",
    "delete": "Delete",
    "cwra_directory_creation_permission_denied": "Directory creation permission denied",
    "cwra_entry_not_found": "Entry not found",
    "cwra_file_renaming_permission_denied": "File renaming permission denied",
    "cwra_entry_removal_permission_denied": "Entry removal permission denied"
  },
  "fr": {
    "load": "Charger",
    "save": "Enregistrer",
    "filename": "Nom du fichier",
    "directory": "Répertoire",
    "enter_location_manually": "Entrer le chemin à la main...",
    "nonexisting_path": "Le chemin entré n'existe pas",
    "folder_creation_permission_denied": "Autorisation refusée pour la création d'un nouveau dossier",
    "cwra_directory_listing_permission_denied": "Autorisation refusée pour lister le contenu d'un répertoire",
    "unknown_error": "Erreur indéterminée",
    "mkdir": "Créer un nouveau dossier",
    "new_folder": "Nouveau dossier",
    "confirm_deletion": "Etes vous sûr de vouloir supprimer le fichier {entry_name} dans le dossier {path} ?",
    "item": "élément | éléments",
    "unknown_number_of_items": "? éléments(s)",
    "confirm_renaming": "Confirmer le renommage",
    "cancel_renaming": "Annuler le renommage",
    "rename": "Renommer",
    "delete": "Supprimer",
    "cwra_directory_creation_permission_denied": "Permission refusée pour la création du répertoire",
    "cwra_entry_not_found": "Entrée inexistante",
    "cwra_file_renaming_permission_denied": "Permission refusée pour le renommage du fichier",
    "cwra_entry_removal_permission_denied": "Permission refusée pour la suppression de cette entrée"
  }
}
</i18n>

<style lang="scss" scoped>
@import '@/custom-bootstrap.scss';
.stretcher {
  position: absolute;
  height: auto;
  top: 0;
  bottom: 0;
}

.cursor_default {
  cursor: default;
  user-select: none;
}
.selected_item {
  background-color: $lightblue;
}
.folder_color {
  color: $orange;
}
.file_color {
  color: $darkgrey;
}
</style>

<script>
import _ from 'lodash'
import $ from 'jquery'
import moment from 'moment'
import store from '@/store'

import Vue from 'vue'
import { mapState } from 'vuex'

import {
  bytes_to_human,
  scrolly_into_view,
  formatted_integer_with_thousand_spaces,
} from '@/utils.js'
import {
  filesystem_listdir,
  filesystem_mkdir,
  filesystem_rename,
  filesystem_remove,
} from '@/api/subscriptions.js'
import { unserializer } from '@/components/object_model.js'

import CBusy from '@/components/c_busy.vue'
import COverlay from '@/components/c_overlay.vue'
// *****************************************************************************

// Time between two refreshes of directory (in milliseconds)
const directory_refresh_timing_threshold = 5 * 1000

// *****************************************************************************

function extend_directory_entry(i18n, entry) {
  const formatted_last_modified_dt = entry.last_modified_dt.calendar()
  const tooltip_last_modified_dt = `${formatted_last_modified_dt} (${entry.last_modified_dt.format()})`
  const size = entry.size
  const is_dir = entry.is_dir
  let formatted_size, tooltip_size
  if (is_dir) {
    formatted_size = i18n.t('unknown_number_of_items')
    if (!_.isNull(size)) {
      let formatted_size_number = size
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      formatted_size = `${formatted_size_number} ${i18n.tc('item', size)}`
    }
    tooltip_size = formatted_size
  } else {
    if (!_.isUndefined(size)) {
      formatted_size = bytes_to_human(size)
      const spaced_size = formatted_integer_with_thousand_spaces(size)
      tooltip_size = `${formatted_size} (${spaced_size} B)`
    }
  }
  return {
    ...entry,
    ...{
      formatted_last_modified_dt,
      tooltip_last_modified_dt,
      formatted_size,
      tooltip_size,
    },
  }
}

function sorted_get_is_dir(x, sort_desc) {
  const is_dir = x.is_dir
  const res = sort_desc ? (is_dir ? 1 : -1) : is_dir ? -1 : 1
  return res
}

function sorted_get_normalized_field(x, field) {
  let res = x[field]
  if (_.isNull(res) && field === 'size') {
    res = -1
  }
  return res
}

function split_path(path) {
  // splits a path into [directory, filename]
  // directory holds the terminating pathsep so that
  // directory + filename still makes a valid path

  const path_separator = store.state.server_path_separator
  const index = path.lastIndexOf(path_separator) + 1
  const directory = path.substr(0, index)
  const filename = path.substr(index, path.length)
  const res = [directory, filename]
  return res
}

function force_bvtable_select_row(
  component,
  table_ref,
  row_index,
  nb_tries = 5
) {
  // This function circumvents an implementation bug from bootstrap-vue
  // which needs its computed to be finished for accepting to select a row,
  // which means we cannot both change the listing and select a row
  // during the same Vue 'tick'
  let retry = nb_tries !== 0
  const bvtable = component.$refs[table_ref]
  if (!_.isUndefined(bvtable)) {
    bvtable.selectRow(row_index)
    const is_selected = bvtable.isRowSelected(row_index)
    retry = retry && !is_selected
    if (is_selected) {
      const cell = component.$refs['name_cell_' + row_index]
      const html_row = $(cell.$el).parents('tr')
      const html_table = html_row.parents('table').parent()
      scrolly_into_view(html_table, html_row)
    }
  }
  if (retry) {
    Vue.nextTick(() =>
      force_bvtable_select_row(component, table_ref, row_index, nb_tries - 1)
    )
  }
}

// *****************************************************************************

export default {
  name: 'FileExplorer',
  components: {
    CBusy,
    COverlay,
  },
  model: {
    prop: 'show',
    event: 'input',
  },
  props: ['show', 'path', 'is_open'],
  data() {
    return {
      local_show: false,

      last_directory_refresh_date: new Date(2000, 1, 1),
      local_directory: store.state.server_path_separator,
      local_filename: '',

      alert_text: '',
      show_manual_directory_input: false,
      manual_directory: '',

      listing_fields: [
        { key: 'name', label: 'Name', class: 'py-0', sortable: true },
        {
          key: 'last_modified_dt',
          label: 'Modified',
          class: 'w-col-2 align-middle py-0',
          sortable: true,
        },
        {
          key: 'size',
          label: 'Size',
          class: 'w-col-2 align-middle text-right py-0',
          sortable: true,
        },
      ],
      fetching_listing: false,
      listing: [],

      selected_index: -1,
      // We need to keep the editing entry index and not simply a flag
      editing_entry_index: -1,
      entry_input: '',

      hovered_index: -1,
    }
  },
  computed: {
    action_title() {
      let res
      if (this.is_open) {
        res = this.$t('load')
      } else {
        res = this.$t('save')
      }
      return res
    },
    local_path() {
      return this.local_directory + this.local_filename
    },
    breadcrumb_items() {
      let res = this.local_directory.split(this.server_path_separator)
      res = res.slice(1, -1)
      return res
    },
    ...mapState(['server_path_separator']),
  },

  watch: {
    show: {
      immediate: true,
      handler(nv, ov) {
        if (nv !== ov) {
          this.local_show = nv
        }
      },
    },
    local_show: {
      immediate: true,
      handler(nv, ov) {
        if (nv && !ov) {
          this.last_directory_refresh_date = new Date(2000, 1, 1)
          this.refresh_directory_listing_and_select(this.path)
        }
        if (nv !== this.show) {
          this.$emit('input', nv)
        }
      },
    },
    path(nv, ov) {
      if (nv !== ov) {
        this.refresh_directory_listing_and_select(nv)
      }
    },
  },

  mounted() {
    this.refresh_directory_listing_and_select(this.path)
  },

  methods: {
    icon_classes(is_dir) {
      let res = ['fad']
      if (is_dir) {
        res.push('fa-folder')
        res.push('folder_color')
      } else {
        res.push('fa-file-alt')
        res.push('file_color')
      }
      return res
    },

    child_path(subdirectory_name) {
      let res = this.local_directory
      if (!_.isEqual(res.substr(-1), this.server_path_separator)) {
        res += this.server_path_separator
      }
      res += subdirectory_name
      res += this.server_path_separator
      return res
    },

    go_to(index) {
      let path
      if (index === -1) {
        path = this.server_path_separator
      } else {
        path =
          this.server_path_separator +
          this.breadcrumb_items
            .slice(0, index + 1)
            .join(this.server_path_separator) +
          this.server_path_separator
      }
      this.refresh_directory_listing_and_select(path)
    },

    compute_listing(raw_listing) {
      let res = []
      let rid = 0
      let visibility = false
      const i18n = this.$i18n
      for (const directory of raw_listing) {
        let [is_dir, name, last_modified_dt, size] = directory
        let entry = { rid, name, last_modified_dt, size, is_dir, visibility }
        res.push(extend_directory_entry(i18n, entry))
        rid += 1
      }
      Vue.set(this, 'listing', res)
    },

    async refresh_directory_listing_and_select(path) {
      if (!this.local_show) {
        return
      }
      if (_.isUndefined(path) || path.length === 0) {
        path = store.state.server_path_separator
      }
      if (
        this.local_path === path &&
        new Date() - this.last_directory_refresh_dt <=
          directory_refresh_timing_threshold
      ) {
        return
      }
      const [directory, filename] = split_path(path)
      this.manual_directory = directory

      this.listing = []
      this.selected_index = -1
      this.editing_entry = false
      this.alert_text = ''
      this.local_directory = directory
      try {
        this.fetching_listing = true
        const raw_listing = await filesystem_listdir(directory)
        this.compute_listing(raw_listing)
      } catch (err) {
        let terr = unserializer.visit(err.response.data)
        this.alert_text = this.$t(terr.args[0])
      } finally {
        this.fetching_listing = false
      }
      this.last_directory_refresh_dt = new Date()
      this.select_filename(filename)
    },

    select_filename(filename) {
      this.local_filename = filename
      if (filename != '') {
        const index = (_.find(this.listing, { name: filename }) || {}).rid
        if (!_.isUndefined(index)) {
          force_bvtable_select_row(this, 'table', index)
        }
      }
    },

    row_selected(items) {
      let selected_index = -1
      if (items.length === 1) {
        const item = items[0]
        selected_index = item.rid
        this.local_filename = item.name
      }
      const old_selected_index = this.selected_index
      this.selected_index = selected_index
      let old_editing_entry_index = -1
      if (selected_index !== this.editing_entry_index) {
        old_editing_entry_index = this.editing_entry_index
        this.editing_entry_index = -1
      }
      this.update_row_visibility(old_selected_index)
      this.update_row_visibility(selected_index)
      this.update_row_visibility(old_editing_entry_index)
    },

    async manual_directory_validated() {
      let directory = this.manual_directory
      this.show_manual_directory_input = false
      this.refresh_directory_listing_and_select(directory)
    },

    async mkdir() {
      try {
        const new_folder = await filesystem_mkdir(
          this.local_directory,
          this.$t('new_folder')
        )
        await this.refresh_directory_listing_and_select(new_folder)
      } catch (err) {
        let terr = unserializer.visit(err.response.data)

        this.$bvToast.toast(terr.args[1], {
          title: this.$t(terr.args[0]),
          toaster: 'b-toaster-top-full',
          solid: true,
          appendToast: false,
          variant: 'danger',
        })
      }
    },

    async delete_entry(index) {
      const directory = this.local_directory
      let entry_name = this.listing[index].name
      let res = await this.$bvModal.msgBoxConfirm(
        this.$t('confirm_deletion', { directory, entry_name })
      )
      if (res) {
        try {
          await filesystem_remove(directory, entry_name)
          await this.refresh_directory_listing_and_select(directory)
        } catch (err) {
          let terr = unserializer.visit(err.response.data)
          this.$bvToast.toast(terr.args[1], {
            title: this.$t(terr.args[0]),
            toaster: 'b-toaster-top-full',
            solid: true,
            appendToast: false,
            variant: 'danger',
          })
        }
      }
    },

    show_entry_input(index) {
      this.editing_entry_index = index
      this.entry_input = this.listing[index].name
      if (index !== this.selected_index) {
        this.$refs.table.selectRow(index)
      }
    },

    cancel_entry_input() {
      this.editing_entry_index = -1
    },

    async validate_entry_input() {
      const index = this.editing_entry_index
      this.editing_entry_index = -1
      const directory = this.local_directory
      const new_entry_name = this.entry_input
      const old_entry_name = this.listing[index].name
      if (old_entry_name !== new_entry_name) {
        try {
          await filesystem_rename(directory, new_entry_name, old_entry_name)
          await this.refresh_directory_listing_and_select(directory)
        } catch (err) {
          let terr = unserializer.visit(err.response.data)

          this.$bvToast.toast(terr.args[1], {
            title: this.$t(terr.args[0]),
            toaster: 'b-toaster-top-full',
            solid: true,
            appendToast: false,
            variant: 'danger',
          })
        }
      }
    },

    validate() {
      if (this.local_filename.length === 0) {
        return
      }
      const item = _.find(this.listing, { name: this.local_filename })
      if (!_.isUndefined(item) && item.is_dir) {
        this.refresh_directory_listing_and_select(this.child_path(item.name))
      } else {
        let res = this.local_path
        this.$emit('change', res)
        this.$nextTick(() => {
          this.$refs.modal.hide()
        })
      }
    },

    ok_clicked(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.validate()
    },

    row_double_clicked(item, index, event) {
      this.validate()
    },

    update_row_visibility(index) {
      if (index === -1) {
        return
      }
      let item = this.listing[index]
      if (_.isUndefined(item)) {
        return
      }
      let visibility = false
      if (index === this.hovered_index || index === this.selected_index) {
        visibility = true
      }
      item.visibility = visibility
    },

    row_hovered(item, index, event) {
      let old_hovered_index = this.hovered_index
      this.hovered_index = item.rid
      this.update_row_visibility(old_hovered_index)
      this.update_row_visibility(this.hovered_index)
    },

    row_unhovered(item, index, event) {
      let old_hovered_index = this.hovered_index
      this.hovered_index = -1
      this.update_row_visibility(old_hovered_index)
      this.update_row_visibility(this.hovered_index)
    },

    directory_listing_sort_compare(
      lhs,
      rhs,
      field,
      sort_desc,
      formatter,
      options,
      locale
    ) {
      let res
      let x = sorted_get_is_dir(lhs, sort_desc)
      let y = sorted_get_is_dir(rhs, sort_desc)
      if (x === y) {
        x = sorted_get_normalized_field(lhs, field)
        y = sorted_get_normalized_field(rhs, field)
      }
      res = x < y ? -1 : x > y ? 1 : 0
      return res
    },
  },
}
</script>
